import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSchemaCellRenderers } from "../hooks/useSchemaCellRenderers";
import { useSchemaRowClick } from "../hooks/useSchemaRowClick";
import { useSchemaTableData } from "../hooks/useSchemaTableData";
import type { DataTableComponentProps } from "../types";
import { processColumnsToTableColumns } from "../utils/columnProcessing";

import { DataTable } from "@/components/ui/v2/table/data-table";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import type { TableColumn } from "@/types/table/tableProps";

function DataTableComponent({
  columns,
  rowIdColumn,
  tableData: contextTableData,
  isLoading: contextIsLoading,
  pagination: contextPagination,
  onSort: contextOnSort,
  onRowClick,
  orderBy: contextOrderBy,
  descending: contextDescending,
  emptyMessage: contextEmptyMessage,
  customCellRenderers: contextCustomCellRenderers,
  accessorMap,
  integrationName,
  moduleName,
  clientId,
  tenantId,
}: DataTableComponentProps) {
  const { t } = useTranslation();

  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();

  const moduleTableData = useSchemaTableData(
    integrationName,
    moduleName ?? "",
    clientId,
    tenantId
  );

  const moduleCellRenderers = useSchemaCellRenderers(
    integrationName,
    moduleName ?? "",
    clientId
  );

  const moduleRowClick = useSchemaRowClick(
    integrationName,
    moduleName ?? "",
    clientId
  );

  const tableData = moduleTableData?.tableData ?? contextTableData ?? [];
  const isLoading = moduleTableData
    ? moduleTableData.isLoading || moduleTableData.isFetching
    : contextIsLoading ?? false;
  const totalRecords = moduleTableData?.totalRecords ?? contextPagination?.totalRecords ?? 0;

  const pagination = useMemo(() => {
    if (moduleTableData) {
      return {
        page,
        pageSize,
        totalRecords,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
      };
    }
    return contextPagination;
  }, [moduleTableData, page, pageSize, totalRecords, setPage, setPageSize, contextPagination]);

  const orderBy = moduleTableData?.orderBy ?? contextOrderBy ?? null;
  const descending = moduleTableData?.descending ?? contextDescending ?? false;
  const onSort = moduleTableData?.onSort ?? contextOnSort;

  const emptyMessage = moduleTableData?.emptyMessage ?? contextEmptyMessage ?? t("common.noData");

  const customCellRenderers = moduleCellRenderers ?? contextCustomCellRenderers;

  const effectiveRowClick = moduleRowClick ?? onRowClick;

  const tableColumns = processColumnsToTableColumns(
    columns,
    t,
    accessorMap,
    customCellRenderers,
  ) as TableColumn<unknown>[];

  if (!tableColumns.length) {
    return null;
  }

  return (
    <DataTable
      key={tableColumns.map(c => c.accessor).join(",")}
      data={tableData}
      columns={tableColumns}
      isLoading={isLoading}
      page={pagination?.page}
      pageSize={pagination?.pageSize}
      totalRecords={pagination?.totalRecords}
      onPageChange={pagination?.onPageChange}
      onPageSizeChange={pagination?.onPageSizeChange}
      onSort={onSort}
      onRowClick={effectiveRowClick}
      orderBy={orderBy}
      descending={descending}
      emptyMessage={emptyMessage}
      getRowId={(row, index) => {
        const rowObj = row as Record<string, unknown>;
        if (rowIdColumn) {
          const idValue =
            rowObj[rowIdColumn] ??
            rowObj[rowIdColumn[0].toUpperCase() + rowIdColumn.slice(1)];
          if (idValue != null) return idValue as string | number;
        }
        return (rowObj.id ?? rowObj.clientId ?? index) as string | number;
      }}
    />
  );
}

export { DataTableComponent };
